import React, { useContext, useEffect, useRef, useState } from 'react';

import Column from '@amzn/meridian/column';
import DatePicker from '@amzn/meridian/date-picker';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import { Popover, PopoverHeader } from '@amzn/meridian';

import RefreshTimer from './RefreshTimer';
import RolloverVolumeView from './RolloverVolumeView';
import TotalVolumeView from './TotalVolumeView';
import CubeView from './CubeView';
import HeadcountView from './HeadcountView';
import DynamicShiftPlanBetaGraph from './DynamicShiftPlanBetaGraph';
import DynamicShiftPlanBetaInputs from './DynamicShiftPlanBetaInputs';
import CopyToClipboardButton from '../../commonComponents/CopyToClipboardButton';
import { getStationCode } from '../../utils/networkUtil';
import apis from '../../utils/apis';
import { ASSUMPTION, PLAN_STATUS, PLAN_VERSIONS, TEXTS, ADHERENCE_PLAN_INVALID_STATUSES } from '../../utils/constants';
import {
  chainWalk,
  useInterval,
  getAvailableDates,
  getTodaysDate,
  formatDateForGraph,
  headcountPlanToIndirectHeadcountAllocations,
  isNumberNotInRange
} from '../../utils/helpers';
import { GlobalStateContext } from '../../global/context/GlobalStateContext';

const DynamicShiftPlanBeta = ({ isDynamicShiftPlanEnabled, isDynamicShiftPlanBetaEnabled }) => {
  const { setAsyncError } = useContext(GlobalStateContext);

  const [ofdDate, setOfdDate] = useState(getTodaysDate());
  const [stationTimezone, setStationTimezone] = useState('UTC');
  const [selectedCycle, setSelectedCycle] = useState('CYCLE_1');
  const [isEditMode, setIsEditMode] = useState(false);
  const saveButtonRef = useRef();
  const [isSaveButtonPopoverOpen, setIsSaveButtonPopoverOpen] = useState(false);
  const approveButtonRef = useRef();
  const [isApproveButtonPopoverOpen, setIsApproveButtonPopoverOpen] = useState(false);

  const [assumptions, setAssumptions] = useState({});
  const availableDates = getAvailableDates();

  // DSP last refresh time
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now() / 1000);

  // GET_ADHERENCE API query variables
  const [adherenceData, setAdherenceData] = useState({});
  const [graphedMetric, setGraphedMetric] = useState('volume');
  const [allDynamicPlans, setAllDynamicPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [shiftPlanDynamic, setShiftPlanDynamic] = useState({});
  const [selectedPlanStatus, setSelectedPlanStatus] = useState('');
  const [selectedPlanVersion, setSelectedPlanVersion] = useState('');
  const [stationLocalStablePlanTime, setStationLocalStablePlanTime] = useState('');

  // Dynamic adherence data for all metrics and process types
  const [adherenceGraphData, setAdherenceGraphData] = useState({});
  const [volumeData, setVolumeData] = useState({});

  // Call GET_ADHERENCE API. This function will be passed as reference to the
  // one-minute timer to be set at the mounting of this component.
  const getAdherenceData = (planId = null, planStatus = null, planVersion = null) => {
    (async () => {
      const query = {
        nodeId: getStationCode(),
        ofdDate: ofdDate
      };

      if (planId && planStatus && planVersion && !ADHERENCE_PLAN_INVALID_STATUSES[planVersion].includes(planStatus)) {
        query.planId = planId;
      }

      // Try querying with the planId. If we don't get any results,
      // that means the plan wasn't an assumption and we should re-query
      // without the planId.
      const planIdAdherenceResponse = await apis['GET_ADHERENCE']({ query });
      if (planIdAdherenceResponse['timeWindowAdherences']) {
        setAdherenceData(planIdAdherenceResponse);
      } else {
        const adherenceResponse = await apis['GET_ADHERENCE']({
          query: { nodeId: getStationCode(), ofdDate: ofdDate }
        });
        setAdherenceData(adherenceResponse);
      }
    })();
    // Update the last update time
    setLastUpdateTime(Date.now() / 1000);
  };

  const getDynamicShiftPlan = async () => {
    const selectedDynamicPlan = await apis['GET_PLAN']({
      query: {
        planId: selectedPlanId
      }
    });
    setShiftPlanDynamic(selectedDynamicPlan);
    setStationLocalStablePlanTime(chainWalk(() => selectedDynamicPlan['output']['metadata']['STABLE_PLAN_TIME'], ''));

    if (selectedDynamicPlan['input']) {
      const existingAssumptionData = {};

      if (chainWalk(() => selectedDynamicPlan['input']['inboundPackageProfile']['overrideBasis']['reason'], false)) {
        existingAssumptionData['inboundPackageProfile'] = {
          ...selectedDynamicPlan['input']['inboundPackageProfile'],
          overrideBasis: {
            reason: ASSUMPTION,
            comment: ''
          }
        };
      }
      if (chainWalk(() => selectedDynamicPlan['input']['outboundPackageProfile']['overrideBasis']['reason'], false)) {
        existingAssumptionData['outboundPackageProfile'] = {
          ...selectedDynamicPlan['input']['outboundPackageProfile'],
          overrideBasis: {
            reason: ASSUMPTION,
            comment: ''
          }
        };
      }
      if (
        chainWalk(() => selectedDynamicPlan['input']['mechanicalConstraintsInput']['overrideBasis']['reason'], false)
      ) {
        existingAssumptionData['mechanicalConstraintsInput'] = {
          ...selectedDynamicPlan['input']['mechanicalConstraintsInput'],
          overrideBasis: {
            reason: ASSUMPTION,
            comment: ''
          }
        };
      }
      if (chainWalk(() => selectedDynamicPlan['input']['opsclockInput']['overrideBasis']['reason'], false)) {
        existingAssumptionData['opsclockInput'] = {
          ...selectedDynamicPlan['input']['opsclockInput'],
          overrideBasis: {
            reason: ASSUMPTION,
            comment: ''
          }
        };
      }
      if (chainWalk(() => selectedDynamicPlan['input']['labourAvailability']['overrideBasis']['reason'], false)) {
        existingAssumptionData['labourAvailability'] = {
          ...selectedDynamicPlan['input']['labourAvailability'],
          overrideBasis: {
            reason: ASSUMPTION,
            comment: ''
          }
        };
      }
      if (
        chainWalk(() => selectedDynamicPlan['input']['productivityByLaborGroupInput']['overrideBasis']['reason'], false)
      ) {
        existingAssumptionData['productivityByLaborGroupInput'] = {
          ...selectedDynamicPlan['input']['productivityByLaborGroupInput'],
          overrideBasis: {
            reason: ASSUMPTION,
            comment: ''
          }
        };
      }
      if (
        chainWalk(
          () => selectedDynamicPlan['input']['productivityByLaborGroupAndCycleInput']['overrideBasis']['reason'],
          false
        )
      ) {
        existingAssumptionData['productivityByLaborGroupAndCycleInput'] = {
          ...selectedDynamicPlan['input']['productivityByLaborGroupAndCycleInput'],
          overrideBasis: {
            reason: ASSUMPTION,
            comment: ''
          }
        };
      }
      // Data for indirect headcount overrides
      // NOTE: Data to be rendered in the UI will come ALWAYS from output -> headcountPlan.
      // a. If there IS NOT any indirect headcount allocations present in the input,
      //    nothing will be added here, conversion from headcount plan to indirect headcount
      //    allocations will be done in the inputs page (i.e. original plan, plan with NO overrides)
      // b. If there IS any indirect headcount allocations present in the input,
      //    head count plan data will be converted into indirect headcount allocations and will be
      //    added here, this is for the inputs page to know there have been previous overrides.
      // Regardless of a or b, when the user clicks the 'Save' button to create a new plan, the
      // indirect headcount allocations in the input will be merged with ONLY those head count
      // allocations rendered from the plan which were edited by the user.
      if (
        chainWalk(
          () => selectedDynamicPlan['input']['indirectHeadCountAllocationsListInput']['overrideBasis']['reason'],
          false
        )
      ) {
        const headcountPlan = selectedDynamicPlan['output']['headCountPlan']['headCountCycles'];
        const initialIndirectHeadcountAllocations = headcountPlanToIndirectHeadcountAllocations(headcountPlan);
        existingAssumptionData['indirectHeadCountAllocationsListInput'] = {
          indirectHeadCountAllocationList: initialIndirectHeadcountAllocations,
          overrideBasis: {
            reason: ASSUMPTION,
            comment: ''
          }
        };
      }
      setAssumptions(existingAssumptionData);
    }
    setIsEditMode(false);
  };

  // NOTE: STATIC_V1 plans are now also being added to the list as
  //       visualizations and planned vs actual data will be released
  //       first for planned data from static plan.
  // NO renaming of functions and/or variables is done for now to
  // reduce extension of changes.
  const filterAndSortPlans = (allPlans) => {
    return allPlans
      .filter(
        (plan) =>
          (plan.planVersion === PLAN_VERSIONS.STATIC_V1 ||
            (plan.planVersion === PLAN_VERSIONS.DYNAMIC_V2 && isDynamicShiftPlanEnabled)) &&
          (plan.status === PLAN_STATUS.APPROVED ||
            plan.status === PLAN_STATUS.PENDING_APPROVAL ||
            plan.status === PLAN_STATUS.PENDING_CREATION ||
            plan.status === PLAN_STATUS.CLOSED)
      )
      .sort((plan1, plan2) => plan2.createdAt - plan1.createdAt);
  };

  const getDynamicShiftPlans = () => {
    if (!isDynamicShiftPlanBetaEnabled) {
      return;
    }

    (async () => {
      const allPlans = (
        await apis['GET_ALL_PLANS']({
          query: {
            date: ofdDate,
            nodeId: getStationCode()
          }
        })
      )['shiftPlans'];

      const dynamicPlans = filterAndSortPlans(allPlans);
      setAllDynamicPlans(dynamicPlans);

      if (dynamicPlans.length > 0) {
        // Copy list of plans to perform selection without altering original sort
        const plansForSelection = JSON.parse(JSON.stringify(dynamicPlans));
        const selectedDynamicPlan = plansForSelection.sort((firstPlan, secondPlan) => {
          function getStatusPriority(planStatus) {
            switch (planStatus) {
              case PLAN_STATUS.PENDING_CREATION:
                return 1;
              case PLAN_STATUS.APPROVED:
                return 2;
              case PLAN_STATUS.PENDING_APPROVAL:
                return 3;
              default:
                return 99;
            }
          }

          const firstPriority = getStatusPriority(firstPlan.status);
          const secondPriority = getStatusPriority(secondPlan.status);

          return firstPriority === secondPriority
            ? dynamicPlans.indexOf(firstPlan) - dynamicPlans.indexOf(secondPlan)
            : firstPriority - secondPriority;
        })[0];
        setSelectedPlanId(selectedDynamicPlan['planId']);
        setSelectedPlanStatus(selectedDynamicPlan['status']);
        setSelectedPlanVersion(selectedDynamicPlan['planVersion']);
      } else {
        setSelectedPlanId('');
        setSelectedPlanStatus('');
        setSelectedPlanVersion('');
        setAssumptions({});
        setShiftPlanDynamic({});
      }
    })();
  };

  // Update state variable with current station timezone
  // Try to get timezone from shiftplan, if not available attempt
  // to get it from the adherence data, if not available leave the
  // last value (do not update). Initial default value set to 'UTC'
  const updateStationTimezone = () => {
    let newStationTimezone = shiftPlanDynamic?.input?.nodeZoneId;
    if (newStationTimezone === null || newStationTimezone === undefined || newStationTimezone === '') {
      newStationTimezone = adherenceData?.stationAttrs?.timezone;
    }
    if (newStationTimezone !== null || newStationTimezone !== undefined || newStationTimezone !== '') {
      setStationTimezone(newStationTimezone);
    }
  };

  useEffect(() => {
    (async () => {
      setShiftPlanDynamic({});
      setAssumptions({});

      if (selectedPlanId) {
        await getDynamicShiftPlan();
        getAdherenceData(selectedPlanId, selectedPlanStatus, selectedPlanVersion);
        updateStationTimezone();
      }
    })();
  }, [selectedPlanId, selectedPlanStatus]);

  // Mounting actions (executing just once))
  useEffect(() => {
    getAdherenceData();
  }, []);

  useInterval(() => {
    if (!isEditMode) {
      getAdherenceData(selectedPlanId, selectedPlanStatus, selectedPlanVersion);
    }
  }, 60 * 1000);

  useInterval(() => {
    if (!isEditMode && selectedPlanId && selectedPlanStatus === PLAN_STATUS.PENDING_CREATION) {
      getDynamicShiftPlans();
    }
  }, 30 * 1000);

  useEffect(() => {
    setAdherenceData({});
    getAdherenceData();
    getDynamicShiftPlans();
  }, [ofdDate]);

  // Extracts adherence values for the specified valueType ('actual' or 'planned')
  // from the adherence timeWindow object, supporting variable number of process types
  const getAdherenceValues = (valueType, timeWindow) => {
    const result = {};

    // Check if timeWindow object contains specified valueType for overall adherence
    if (
      timeWindow['overallAdherence'] &&
      valueType in timeWindow['overallAdherence'] &&
      timeWindow['overallAdherence'][valueType] !== null
    ) {
      result['Overall'] = timeWindow['overallAdherence'][valueType];
    }

    // Extract values from all process adherences
    if (timeWindow['processAdherences']) {
      timeWindow['processAdherences'].forEach((processItem) => {
        if (valueType in processItem && processItem[valueType] !== null) {
          const processKey = processItem['entityAttrs']['process'];
          result[processKey] = processItem[valueType];
        }
      });
    }

    return result;
  };

  // Evaluates time period of last collected data point against time period
  // of previous collected points to determine time overlapping points
  // and fixes the list so it only contains non-overlapping points
  const addDataWithOverlapHandling = (dataArray, newDataPoint) => {
    const newDuration = newDataPoint.endInSeconds - newDataPoint.startInSeconds;

    // Find overlapping entries
    const overlappingIndices = [];
    dataArray.forEach((existingPoint, index) => {
      const hasOverlap =
        newDataPoint.startInSeconds < existingPoint.endInSeconds &&
        existingPoint.startInSeconds < newDataPoint.endInSeconds;
      if (hasOverlap) {
        overlappingIndices.push(index);
      }
    });

    if (overlappingIndices.length === 0) {
      // No overlaps, just add the new data point
      dataArray.push(newDataPoint);
    } else {
      // Check if new point has wider time window than any overlapping entries
      let shouldKeepNew = true;

      for (const index of overlappingIndices) {
        const existingPoint = dataArray[index];
        const existingDuration = existingPoint.endInSeconds - existingPoint.startInSeconds;

        if (existingDuration >= newDuration) {
          // Existing point has wider or equal time window, don't add new point
          shouldKeepNew = false;
          break;
        }
      }

      if (shouldKeepNew) {
        // Remove all overlapping entries (going backwards to maintain indices)
        overlappingIndices
          .sort((a, b) => b - a)
          .forEach((index) => {
            dataArray.splice(index, 1);
          });
        dataArray.push(newDataPoint);
      }
    }
  };

  // Processes headcount adherence time window objects. Objects with active shift equal to 'ALL_CYCLES' are
  // actual data while objects with any other shift value represent planned data which has to be aggregated
  const aggregateHeadcountGraphData = (hcAdherenceTimeWindows) => {
    // Get copy of time windows with active shift equal to ALL_CYCLES (actual data)
    const actualDataWindows = JSON.parse(
      JSON.stringify(hcAdherenceTimeWindows.filter((timeWindow) => timeWindow['activeShifts'][0] === 'ALL_CYCLES'))
    );

    // Get windows with active shift not equal to ALL_CYCLES (planned data)
    const plannedDataWindows = hcAdherenceTimeWindows.filter(
      (timeWindow) => timeWindow['activeShifts'][0] !== 'ALL_CYCLES'
    );

    // Aggregate planned headcount adherence time windows
    const hcAdTimeWindowsPlanned = (plannedDataWindows ?? []).reduce((result, adTimeWindow) => {
      const timeWindowKey =
        adTimeWindow['timeWindow']['startInSeconds'] + '-' + adTimeWindow['timeWindow']['endInSeconds'];
      if (!result.has(timeWindowKey)) {
        // Create new time window object
        const newAdTimeWindow = JSON.parse(JSON.stringify(adTimeWindow));
        result.set(timeWindowKey, newAdTimeWindow);
      } else {
        const merged = result.get(timeWindowKey);
        // Set active shift to 'AGGREGATED_CYCLES' (indicating this is planned aggregated data)
        merged['activeShifts'][0] = 'AGGREGATED_CYCLES';
        // Aggregate overall planned values
        merged['overallAdherence']['planned'] += adTimeWindow['overallAdherence']['planned'];

        // Combine processAdherence objects
        adTimeWindow['processAdherences'].forEach((processAdherence) => {
          const existingIndex = merged['processAdherences'].findIndex(
            (existing) =>
              existing['entity'] === processAdherence['entity'] &&
              existing['entityAttrs']['department'] === processAdherence['entityAttrs']['department'] &&
              existing['entityAttrs']['process'] === processAdherence['entityAttrs']['process']
          );
          if (existingIndex >= 0) {
            merged['processAdherences'][existingIndex]['planned'] += processAdherence['planned'];
          } else {
            merged['processAdherences'].push(processAdherence);
          }
        });
        result.set(timeWindowKey, merged);
      }
      return result;
    }, new Map());

    // Return concatenated values
    return [...actualDataWindows, ...Array.from(hcAdTimeWindowsPlanned.values())];
  };

  // Extracts from a list of adherence time window objects, for the specified type and cycle,
  // the fields with the actual and planned values to be used for plotting the corresponding graphs
  const getGraphDataFromAdherenceTimeWindows = (adherenceTimeWindows, type, cycle) => {
    // Get and sort adherence time window objects for the specified type
    const typeAdTimeWindows = (adherenceTimeWindows ?? [])
      .filter((adTimeWindow) => adTimeWindow['type'] === type)
      .sort(
        (prevAdTimeWindow, nextAdTimeWindow) =>
          prevAdTimeWindow['timeWindow']['startInSeconds'] - nextAdTimeWindow['timeWindow']['startInSeconds']
      );
    // If type is 'headcount', planned data must be aggregated,
    // otherwise, it must filtered for the specified cycle
    const graphAdTimeWindows =
      type === 'headcount'
        ? aggregateHeadcountGraphData(typeAdTimeWindows)
        : typeAdTimeWindows.filter((adTimeWindow) => adTimeWindow['activeShifts'][0] === cycle);

    // Collect all unique process keys from all time windows
    const allProcessKeys = new Set();
    graphAdTimeWindows.forEach((adTimeWindow) => {
      ['actual', 'planned'].forEach((valueType) => {
        const adherenceValues = getAdherenceValues(valueType, adTimeWindow);
        Object.keys(adherenceValues).forEach((key) => allProcessKeys.add(key));
      });
    });

    // Initialize result object with process-first structure
    const initialResult = {};
    allProcessKeys.forEach((processKey) => {
      initialResult[processKey] = { actual: [], planned: [] };
    });

    const graphData = graphAdTimeWindows.reduce((result, adTimeWindow) => {
      ['actual', 'planned'].forEach((valueType) => {
        const adherenceValues = getAdherenceValues(valueType, adTimeWindow);

        // Add values to appropriate result fields for all process keys
        Object.entries(adherenceValues).forEach(([processKey, value]) => {
          if (value !== null) {
            if (result[processKey]) {
              const dataPoint = {
                startInSeconds: adTimeWindow['timeWindow']['startInSeconds'],
                endInSeconds: adTimeWindow['timeWindow']['endInSeconds'],
                value: value
              };

              // TODO: tujasn. Remove once backend is fixed.
              // For Headcount actuals, we have data at both a 1m and 15m granularity.
              // This causes issues since the overallAdherence object for 1m is populated
              // but for 15m is incorrectly defaulting to 0 instead of null.
              // Once that is fixed, we need to come up with better `addDataWithOverlapHandling` logic
              // or have the backend also query for that data in a 15m period.
              if (valueType === 'actual' && type === 'headcount' && processKey === 'Overall') {
                result[processKey][valueType].push(dataPoint);
              } else {
                addDataWithOverlapHandling(result[processKey][valueType], dataPoint);
              }
            }
          }
        });
      });
      return result;
    }, initialResult);

    // Clean data to remove leading and trailing empty values.
    Object.entries(graphData).forEach(([processKey, processData]) => {
      ['actual', 'planned'].forEach((valueType) => {
        const dataSet = processData[valueType];
        let firstValueIndex = dataSet.length - 1;
        let lastValueIndex = 0;

        for (let i = 0; i < dataSet.length - 1; i++) {
          if (dataSet[i].value !== 0) {
            firstValueIndex = i;
            break;
          }
        }

        for (let i = dataSet.length - 1; i > 1; i--) {
          if (dataSet[i].value !== 0 || dataSet[i].value !== dataSet[i - 1].value) {
            lastValueIndex = i + 1;
            break;
          }
        }
        graphData[processKey][valueType] = dataSet.slice(firstValueIndex, lastValueIndex);
      });
    });

    return graphData;
  };

  // Extracts the breaks data from the list of adherence time window objects,
  // It extracts and collects the data from all metrics to produce ine single
  // set without duplicate data points.
  const getBreaksGraphDataFromAdherenceTimeWindows = (adherenceTimeWindows) => {
    // Get and sort adherence time window objects with breaks data
    const breaksAdTimeWindows = (adherenceTimeWindows ?? []).filter(
      (adTimeWindow) =>
        adTimeWindow['breaks'] !== null && adTimeWindow['breaks'] !== undefined && adTimeWindow['breaks'].length > 0
    );
    // Collect breaks data from adherence time windows removing duplicates
    const breaksData = {};
    breaksAdTimeWindows.forEach((adTimeWindow) => {
      adTimeWindow['breaks'].forEach((breakItem) => {
        const breakKey = breakItem['timeWindow']['startInSeconds'];
        const dataPoint = {
          startInSeconds: breakItem['timeWindow']['startInSeconds'],
          endInSeconds: breakItem['timeWindow']['endInSeconds'],
          value: 0
        };
        breaksData[breakKey] = dataPoint;
      });
    });

    // Get breaks datapoints as a list
    const graphData = [];
    Object.entries(breaksData).forEach(([_, valueObject]) => {
      graphData.push(valueObject);
    });
    graphData.sort((prevBreak, nextBreak) => prevBreak['startInSeconds'] - nextBreak['startInSeconds']);

    return graphData;
  };

  // Accumulate values in data using a running sum
  const accumulated = (data) => {
    return data.reduce((acc, curr) => {
      const sum = (acc.length > 0 ? acc[acc.length - 1]['value'] : 0) + curr['value'];
      acc.push({
        startInSeconds: curr['startInSeconds'],
        endInSeconds: curr['endInSeconds'],
        value: sum
      });
      return acc;
    }, []);
  };

  // For planned cumulative data, we can't do a simple accumulation.
  // If a previous interval processes less volume, that delta will be
  // distributed to future intervals, leading to a higher "cumulative" metric.
  // To counteract that, we'll follow the plan logic and cumulate past actuals then
  // add the planned interval for each interval.
  const accumulatedFromActuals = (planned, actualAccumulated) => {
    const accumulated = [];

    planned.forEach((plannedDatum) => {
      const actualAccumulatedIndex = actualAccumulated.findIndex(
        (item) => item.startInSeconds === plannedDatum.startInSeconds
      );
      if (actualAccumulatedIndex > 0) {
        const accumulatedValue = actualAccumulated[actualAccumulatedIndex - 1].value + plannedDatum.value;

        accumulated.push({
          startInSeconds: plannedDatum['startInSeconds'],
          endInSeconds: plannedDatum['endInSeconds'],
          value: accumulatedValue
        });
      } else {
        const precedingIntervalValue = chainWalk(
          () => accumulated.find((accumulated) => accumulated.endInSeconds === plannedDatum['startInSeconds']).value,
          0
        );
        accumulated.push({
          startInSeconds: plannedDatum['startInSeconds'],
          endInSeconds: plannedDatum['endInSeconds'],
          value: chainWalk(() => plannedDatum.value + precedingIntervalValue, 0)
        });
      }
    });

    return accumulated;
  };

  // Processes adherence data API response to produce data lists suitable
  // for metric viewers and graph components
  useEffect(() => {
    const newAdherenceGraphData = {};

    // Process headcount data
    newAdherenceGraphData.headcount = getGraphDataFromAdherenceTimeWindows(
      adherenceData['timeWindowAdherences'],
      'headcount'
    );

    // Process volume data
    const volumeData = getGraphDataFromAdherenceTimeWindows(
      adherenceData['timeWindowAdherences'],
      'volume',
      selectedCycle
    );
    setVolumeData(volumeData);
    newAdherenceGraphData.volume = {};
    Object.entries(volumeData).forEach(([processKey, processData]) => {
      newAdherenceGraphData.volume[processKey] = {
        actual: accumulated(processData.actual || []),
        // planned: accumulatedFromActuals(processData.planned || [], accumulated(processData.actual || []))
        planned: accumulated(processData.planned || [])
      };
    });

    // Process rate data
    newAdherenceGraphData.rate = getGraphDataFromAdherenceTimeWindows(
      adherenceData['timeWindowAdherences'],
      'rate',
      selectedCycle
    );

    // Process breaks data
    newAdherenceGraphData.breaks = getBreaksGraphDataFromAdherenceTimeWindows(adherenceData['timeWindowAdherences']);

    updateStationTimezone();
    setAdherenceGraphData(newAdherenceGraphData);
  }, [adherenceData, selectedCycle, shiftPlanDynamic]);

  // Call API to create a new shiftPlan with any overriden values entered
  // by the user. Indirect headcount allocations need a specific pre-processing
  // as they need to be merged with the originall received ones in the shiftPlan.
  const createNewShiftPlan = async () => {
    // Indirect headcount allocations prior to calling api
    if ('indirectHeadCountAllocationsListInput' in assumptions) {
      // Filter headcount allocations in assumptions to get only those modified by user
      const overriddenHeadcountAllocations = assumptions['indirectHeadCountAllocationsListInput'][
        'indirectHeadCountAllocationList'
      ]
        .filter((hcAllocation) => hcAllocation['isOverriden'] === true)
        .map((hcAllocation) => {
          return {
            ...hcAllocation,
            startTime: hcAllocation['startTime'] + ':00',
            endTime: hcAllocation['endTime'] + ':00'
          };
        });
      // Merge original allocations with the overriden ones
      const inputIndirectHeadcoountAllocations = chainWalk(
        () => shiftPlanDynamic['input']['indirectHeadCountAllocationsListInput']['indirectHeadCountAllocationList'],
        []
      ).map((hcAllocation) => {
        return {
          ...hcAllocation,
          isOverriden: false
        };
      });
      const mergedIndirectHeadcountAllocations = [
        ...inputIndirectHeadcoountAllocations,
        ...overriddenHeadcountAllocations
      ];
      // Replace merged allocations in assumptions
      assumptions['indirectHeadCountAllocationsListInput']['indirectHeadCountAllocationList'] =
        mergedIndirectHeadcountAllocations;
    }
    await apis['CREATE_PLAN'](
      {
        body: {
          nodeId: getStationCode(),
          ofdDate: ofdDate,
          planType: 'INTRA_DAY',
          planVersion: PLAN_VERSIONS.DYNAMIC_V2,
          input: assumptions
        }
      },
      setAsyncError
    );

    setIsEditMode(false);
    setIsSaveButtonPopoverOpen(false);

    getDynamicShiftPlans();
  };

  // Render the create dynamic shift plan button
  const createDynamicShiftPlanButton = (
    <Button
      type="primary"
      size="large"
      disabled={!isDynamicShiftPlanEnabled}
      onClick={async () => {
        await apis['CREATE_PLAN'](
          {
            body: {
              nodeId: getStationCode(),
              ofdDate: ofdDate,
              planType: 'INTRA_DAY',
              planVersion: PLAN_VERSIONS.DYNAMIC_V2
            }
          },
          setAsyncError
        );
        getDynamicShiftPlans();
      }}>
      Create Plan{!isDynamicShiftPlanEnabled && ' (coming soon)'}
    </Button>
  );

  // Render the edit/save dynamic shift plan buttons
  const editDynamicShiftPlanButtons = (
    <Row spacing="200">
      {isEditMode ? (
        <Button
          size="small"
          type="tertiary"
          id="btnCancel"
          onClick={() => {
            setAssumptions({});
            setIsEditMode(false);
          }}>
          Cancel
        </Button>
      ) : (
        <div />
      )}
      {isEditMode ? (
        <div>
          <Button type="primary" size="small" onClick={() => setIsSaveButtonPopoverOpen(true)} ref={saveButtonRef}>
            Save
          </Button>
          <Popover
            anchorNode={saveButtonRef.current}
            open={isSaveButtonPopoverOpen}
            onClose={() => setIsSaveButtonPopoverOpen(false)}
            position="bottom">
            <PopoverHeader closeLabel="Close">Confirm changes?</PopoverHeader>
            <Column>
              <Text>Confirming will run an assumption plan.</Text>
              <Button type="tertiary" onClick={createNewShiftPlan}>
                Confirm
              </Button>
            </Column>
          </Popover>
        </div>
      ) : (
        <div />
      )}
      {!isEditMode ? (
        <Button size="small" type="tertiary" onClick={() => setIsEditMode(true)}>
          Edit
        </Button>
      ) : (
        <div />
      )}
      <Button
        size="small"
        disabled={isEditMode || selectedPlanStatus !== PLAN_STATUS.PENDING_APPROVAL}
        ref={approveButtonRef}
        onClick={() => setIsApproveButtonPopoverOpen(true)}>
        Approve
      </Button>
      <Popover
        anchorNode={approveButtonRef.current}
        open={isApproveButtonPopoverOpen}
        onClose={() => setIsApproveButtonPopoverOpen(false)}
        position="bottom">
        <PopoverHeader closeLabel="Close">Approve plan?</PopoverHeader>
        <Column>
          <Button
            onClick={async () => {
              await apis['UPDATE_PLAN_STATUS'](
                {
                  body: {
                    planId: selectedPlanId,
                    status: PLAN_STATUS.APPROVED
                  }
                },
                setAsyncError
              );
              getDynamicShiftPlans();
              setSelectedPlanStatus(PLAN_STATUS.APPROVED, '');
              setIsApproveButtonPopoverOpen(false);
            }}>
            Approve
          </Button>
        </Column>
      </Popover>
    </Row>
  );

  // Filter controls displayed at the top of the DSP page
  const graphFilters = (
    <Column spacing="100" alignmentHorizontal="end">
      <Row alignmentHorizontal="end" alignmentVertical="bottom" width="100%" spacing="500">
        {allDynamicPlans.length === 0 || !isDynamicShiftPlanEnabled
          ? createDynamicShiftPlanButton
          : isDynamicShiftPlanEnabled && editDynamicShiftPlanButtons}
        <Column spacing="300" alignmentHorizontal="end">
          <Row alignmentVertical="center" spacing="300">
            <Text>Out for Delivery Date</Text>
            <DatePicker
              size="small"
              width={200}
              value={ofdDate}
              onChange={setOfdDate}
              monthsInView={1}
              disabledDates={(d) => !availableDates.includes(d)}
            />
          </Row>
          {isDynamicShiftPlanBetaEnabled && (
            <Row alignmentVertical="center" spacing="300">
              <Text>Plan</Text>
              <Select
                value={selectedPlanId}
                onChange={(planId) => {
                  setSelectedPlanId(planId);
                  const selectedPlan = allDynamicPlans.find((plan) => plan.planId === planId);
                  setSelectedPlanStatus(selectedPlan?.status || '');
                  setSelectedPlanVersion(selectedPlan?.planVersion || '');
                }}
                size="small">
                {allDynamicPlans.map((dynamicPlan) => {
                  const displayDate =
                    stationTimezone === null || stationTimezone === undefined || stationTimezone === ''
                      ? formatDateForGraph(dynamicPlan.createdAt)
                      : formatDateForGraph(dynamicPlan.createdAt, stationTimezone);
                  const displayLastUpdatedBy =
                    dynamicPlan.lastUpdatedBy === 'AUTO_TRIGGER' ? '' : ' - ' + dynamicPlan.lastUpdatedBy;
                  return (
                    <SelectOption
                      value={dynamicPlan.planId}
                      label={[
                        displayDate,
                        displayLastUpdatedBy,
                        ' - ',
                        dynamicPlan.planType,
                        ' - ',
                        dynamicPlan.status
                      ].join('')}
                    />
                  );
                })}
              </Select>
            </Row>
          )}
        </Column>
      </Row>
      {isDynamicShiftPlanBetaEnabled && (
        <Text type="b100">
          Plan ID: {selectedPlanId}
          <CopyToClipboardButton value={selectedPlanId} />
        </Text>
      )}
    </Column>
  );

  // TO DO: Finalize overall layout and component details
  return (
    <Column alignmentHorizontal="start" alignmentVertical="top" spacingInset="none 400 400 400" width="100%">
      <Row alignmentHorizontal="justify" widths="fill" width="100%">
        <Row>
          <Column spacing="200" alignmentHorizontal="start">
            <Text type={TEXTS.H1}>Dynamic Shift Plan</Text>
            <RefreshTimer lastUpdateTime={lastUpdateTime} />
          </Column>
          {graphedMetric === 'headcount' ? (
            <Select label="Cycle" disabled={true} value={'All Cycles'} size="xlarge">
              <SelectOption value="All Cycles" label="All Cycles" />
            </Select>
          ) : (
            <Select label="Cycle" value={selectedCycle} onChange={setSelectedCycle} size="xlarge">
              <SelectOption value="CYCLE_1" label="CYCLE_1" />
              <SelectOption value="SAME_DAY_SUNRISE" label="SAME_DAY_SUNRISE" />
              <SelectOption value="SAME_DAY" label="SAME_DAY" />
              <SelectOption value="SAME_DAY_AM" label="SAME_DAY_AM" />
              <SelectOption value="CYCLE_2" label="CYCLE_2" />
            </Select>
          )}
        </Row>
        {graphFilters}
      </Row>
      <br />
      <Row alignmentHorizontal="start" width="100%">
        <RolloverVolumeView shiftPlanDynamic={shiftPlanDynamic} selectedCycle={selectedCycle} />
        <TotalVolumeView selectedCycle={selectedCycle} />
        <CubeView adherenceData={adherenceData} selectedCycle={selectedCycle} />
        <HeadcountView adherenceData={adherenceData} />
      </Row>
      <TabGroup value={graphedMetric} onChange={setGraphedMetric} type="default" fill="line">
        {!!isDynamicShiftPlanEnabled && <Tab value="inputs">Inputs</Tab>}
        <Tab value="volume">Volume</Tab>
        <Tab value="headcount">Headcount</Tab>
        <Tab value="rate">Rate</Tab>
      </TabGroup>
      {graphedMetric === 'inputs' ? (
        <DynamicShiftPlanBetaInputs
          ofdDate={ofdDate}
          stationTimezone={stationTimezone}
          selectedCycle={selectedCycle}
          isEditMode={isEditMode}
          shiftPlanDynamic={shiftPlanDynamic}
          assumptions={assumptions}
          setAssumptions={setAssumptions}
          stationLocalStablePlanTime={stationLocalStablePlanTime}
        />
      ) : (
        <DynamicShiftPlanBetaGraph
          graphedMetric={graphedMetric}
          stationLocalStablePlanTime={stationLocalStablePlanTime}
          adherenceGraphData={adherenceGraphData}
          isDynamicShiftPlanEnabled={isDynamicShiftPlanEnabled}
          isDynamicShiftPlanBetaEnabled={isDynamicShiftPlanBetaEnabled}
        />
      )}
    </Column>
  );
};
export default DynamicShiftPlanBeta;
